import React, { useMemo } from 'react';
import { FilterMenuDualPanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterMenuDualPanelItemProps } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuDualPanel';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { determineNonDefaultGlobalFilterCount } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { GlobalFilterContent } from 'apps/sigma2-ui/src/filters-v2/components/GlobalFilterContent';

export const GlobalFiltersMenu = ({
  closeMenu,
  setGlobalFilters,
  globalFilterDefinitions,
  stagingGlobalFilters,
  setStagingGlobalFilter,
  myObservation,
  selectedGlobalFilterKey,
  setSelectedGlobalFilterKey,
  isEmbedMode,
}: {
  closeMenu: () => void;
  globalFilterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'];
  setGlobalFilters: (params: {
    globalFilterSelections: SecondaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
  stagingGlobalFilters: SecondaryFilterSelection[];
  setStagingGlobalFilter: (params: {
    secondaryFilterKey: string;
    value: FilterStoreValue;
  }) => void;
  myObservation: MyObservation | null;
  selectedGlobalFilterKey: string | null;
  setSelectedGlobalFilterKey: (key: string | null) => void;
  isEmbedMode: boolean;
}) => {
  const language = useLanguage();

  const items: FilterMenuDualPanelItemProps[] = useMemo(() => {
    const allItems: FilterMenuDualPanelItemProps[] = [];

    for (const filterDefinition of globalFilterDefinitions) {
      allItems.push({
        key: filterDefinition.columnHeaderValue,
        translatedContent: language.dangerousUserInput({
          value: filterDefinition.displayName,
        }),
        onClick: () => {
          setSelectedGlobalFilterKey(filterDefinition.columnHeaderValue);
        },
        selected:
          selectedGlobalFilterKey === filterDefinition.columnHeaderValue,
        itemValue: filterDefinition.columnHeaderValue,
        content: (
          <GlobalFilterContent
            stagingGlobalFilters={stagingGlobalFilters}
            filterKey={filterDefinition.columnHeaderValue}
            setStagingGlobalFilter={setStagingGlobalFilter}
            globalFilterDefinitions={globalFilterDefinitions}
            myObservation={myObservation}
          />
        ),
      });
    }

    return allItems;
  }, [
    globalFilterDefinitions,
    language,
    selectedGlobalFilterKey,
    stagingGlobalFilters,
    setStagingGlobalFilter,
    myObservation,
    setSelectedGlobalFilterKey,
  ]);

  // Disable apply button if in embed mode and any staging global filter has an empty array value (i.e., no selection)
  const applyDisabled = useMemo(() => {
    return (
      isEmbedMode &&
      stagingGlobalFilters.some(
        (stagingGlobalFilter) =>
          Array.isArray(stagingGlobalFilter.value) &&
          stagingGlobalFilter.value.length === 0,
      )
    );
  }, [isEmbedMode, stagingGlobalFilters]);

  return (
    <FilterMenuDualPanel
      items={items}
      bottomProps={{
        onApply: () => {
          setGlobalFilters({
            globalFilterSelections: stagingGlobalFilters,
          });

          closeMenu();
        },
        onCancel: () => {
          closeMenu();
        },
        applyDisabled,
        errorTranslatedContent: null,
        appliedCount: determineNonDefaultGlobalFilterCount({
          stagingGlobalFilters,
        }),
      }}
    />
  );
};
